import React from 'react';
import {Box, Chip, Typography, Table, TableBody, TableCell, TableHead, TableRow, Divider, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button} from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface EquilibriumStep {
    label: string;
    action: string;
}

export interface TradingStateDetail {
    state: string;
    title: string;
    description: string;
    action: string;
    priority: number;
    steps: EquilibriumStep[];
}

export interface EquilibriumPosition {
    assetKey: number;
    symbol: string;
    targetQuantity: number;
    currentQuantity: number;
    quantityToExecute: number;
    amountToExecute: number;
    price: number;
    positionType: string;
    orderSide: string;
}

export interface EquilibriumStatus {
    autoPilotSettingsKey: number;
    inEquilibrium: boolean;
    tradingStates: TradingStateDetail[];
    affectedPositions: EquilibriumPosition[];
}

interface EquilibriumStatusCardProps {
    status?: EquilibriumStatus;
    loading?: boolean;
}

const formatCurrency = (value: number): string =>
    `$${value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;

const formatQuantity = (value: number): string =>
    value.toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 4});

const orderSideColor = (side: string): string => (side === 'BUY' ? '#059669' : '#dc2626');

const cardSx = {
    width: '100%',
    p: 2.5,
    bgcolor: 'white',
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'grey.200',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.1)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, #007AFF, #5856D6, #FF2D92)',
    },
} as const;

export const EquilibriumStatusCard: React.FC<EquilibriumStatusCardProps> = ({status, loading}) => {
    const [infoOpen, setInfoOpen] = React.useState(false);

    const infoDialog = (
        <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm">
            <DialogTitle sx={{fontWeight: 700}}>About Equilibrium Status</DialogTitle>
            <DialogContent>
                <DialogContentText component="div" sx={{color: 'grey.800'}}>
                    <p>
                        This UI is <strong>experimental</strong> and will eventually be elevated to users.
                        Engineering, Product, and Mobile are working to give the customer more detail on
                        why they aren&apos;t in equilibrium.
                    </p>
                    <p>
                        If CS is reading this and you see something that doesn&apos;t look right,
                        talk to engineering.
                    </p>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setInfoOpen(false)}>Got it</Button>
            </DialogActions>
        </Dialog>
    );

    if (loading && !status) {
        return (
            <Box sx={cardSx}>
                <Typography variant="body2" color="text.secondary">Loading equilibrium status…</Typography>
            </Box>
        );
    }

    if (!status) {
        return null;
    }

    const sortedStates = [...status.tradingStates].sort((a, b) => a.priority - b.priority);

    return (
        <Box sx={cardSx}>
            {infoDialog}
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                    <Typography variant="h6" sx={{
                        fontWeight: 700,
                        color: 'grey.800',
                        fontSize: '1rem',
                        letterSpacing: '-0.02em',
                    }}>
                        Equilibrium Status
                    </Typography>
                    <Tooltip title="Experimental — click for details">
                        <IconButton
                            size="small"
                            onClick={() => setInfoOpen(true)}
                            sx={{p: 0.25, color: 'grey.400', '&:hover': {color: 'grey.600'}}}
                        >
                            <InfoOutlinedIcon sx={{fontSize: '1rem'}} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Chip
                    icon={status.inEquilibrium ? <CheckCircleOutlinedIcon /> : <ErrorOutlineIcon />}
                    label={status.inEquilibrium ? 'In Equilibrium' : 'Out of Equilibrium'}
                    color={status.inEquilibrium ? 'success' : 'warning'}
                    size="small"
                    sx={{
                        fontWeight: 600,
                        height: '24px',
                        '& .MuiChip-label': {px: 1, fontSize: '0.7rem'},
                        borderRadius: '6px',
                    }}
                />
            </Box>

            {/* Trading States */}
            {sortedStates.length > 0 ? (
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                    {sortedStates.map((ts) => (
                        <Box
                            key={`${ts.state}-${ts.priority}`}
                            sx={{
                                p: 1.5,
                                bgcolor: 'grey.50',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'grey.100',
                            }}
                        >
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap'}}>
                                <Typography variant="body2" sx={{fontWeight: 600, color: 'grey.800', fontSize: '0.8rem'}}>
                                    {ts.title}
                                </Typography>
                                <Chip
                                    label={ts.state}
                                    size="small"
                                    variant="outlined"
                                    sx={{height: '18px', '& .MuiChip-label': {px: 0.75, fontSize: '0.6rem'}}}
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.72rem', mb: ts.steps.length ? 1 : 0}}>
                                {ts.description}
                            </Typography>
                            {ts.action && ts.action !== 'NONE' && (
                                <Chip
                                    label={ts.action.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}
                                    color="primary"
                                    size="small"
                                    sx={{height: '18px', mb: ts.steps.length ? 1 : 0, '& .MuiChip-label': {px: 0.75, fontSize: '0.6rem'}}}
                                />
                            )}
                            {ts.steps.length > 0 && (
                                <Box component="ul" sx={{m: 0, pl: 2}}>
                                    {ts.steps.map((step, idx) => (
                                        <Box component="li" key={idx} sx={{fontSize: '0.7rem', color: 'grey.700'}}>
                                            {step.label}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.75rem'}}>
                    No active trading states.
                </Typography>
            )}

            {/* Affected Positions */}
            {status.affectedPositions.length > 0 && (
                <>
                    <Divider sx={{my: 2}} />
                    <Typography variant="body2" sx={{fontWeight: 600, color: 'grey.700', fontSize: '0.75rem', mb: 1}}>
                        Affected Positions ({status.affectedPositions.length})
                    </Typography>
                    <Table size="small" sx={{'& td, & th': {px: 0.75, py: 0.5, fontSize: '0.7rem', borderColor: 'grey.100'}}}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 600, color: 'grey.600'}}>Symbol</TableCell>
                                <TableCell sx={{fontWeight: 600, color: 'grey.600'}}>Side</TableCell>
                                <TableCell align="right" sx={{fontWeight: 600, color: 'grey.600'}}>Current → Target</TableCell>
                                <TableCell align="right" sx={{fontWeight: 600, color: 'grey.600'}}>Amount</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {status.affectedPositions.map((pos) => (
                                <TableRow key={pos.assetKey}>
                                    <TableCell sx={{fontWeight: 600}}>{pos.symbol}</TableCell>
                                    <TableCell sx={{fontWeight: 600, color: orderSideColor(pos.orderSide)}}>
                                        {pos.orderSide}
                                    </TableCell>
                                    <TableCell align="right">
                                        {formatQuantity(pos.currentQuantity)} → {formatQuantity(pos.targetQuantity)}
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(pos.amountToExecute)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </>
            )}
        </Box>
    );
};
